/**
 * File: DigitField.js
 * Origin: isotect
 * Description: DigitField component for verification code forms.
 *
 * Version: v2.0.0
 * Created: 2025-07-26
 * Last Updated: 2026-04-10
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useState } from "react";

/**
 * DigitField component represents a single input field used for entering
 * one digit in a multi-field verification code form.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} props.fieldID - ID of this DigitField
 * @param {string} props.prevFieldID - ID of the previous DigitField (used for focus management)
 * @param {string} props.nextFieldID - ID of the next DigitField (used for focus management)
 *
 * @returns {JSX.Element} A single-character input field with accessibility labels
 *
 * @example
 * <DigitField
 *   digit={digit1}
 *   setDigit={setDigit1}
 *   fieldID="digit-1"
 *   prevFieldID="digit-0"
 *   nextFieldID="digit-2"
 *   onPaste={handlePaste}
 * />
 */
function DigitField({
    digit,
    setDigit,
    fieldID,
    prevFieldID,
    nextFieldID,
    onPaste,
}) {
    const handleChange = (e) => {
        setDigit(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Backspace" && e.target.value === "") {
            if (prevFieldID !== null) {
                document.getElementById(prevFieldID).focus();
            }
        }
    };

    const handleInput = (e) => {
        if (e.target.value.length === e.target.maxLength) {
            if (nextFieldID !== null) {
                document.getElementById(nextFieldID).focus();
            }
        }
    };

    const handleFocus = () => {
        setDigit("");
    };

    const handlePaste = (e) => {
        onPaste(e);
    };

    return (
        <>
            <div>
                <label htmlFor={fieldID}></label>
                <input
                    className="digit"
                    type="text"
                    inputMode="numeric"
                    name={fieldID}
                    id={fieldID}
                    value={digit}
                    required
                    pattern="[0-9]"
                    maxLength="1"
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onInput={handleInput}
                    onFocus={handleFocus}
                    onPaste={handlePaste}
                />
            </div>
        </>
    );
}

export default DigitField;
